import { BybAvatar } from '@Components/common/byb-ds';
import { BybMenuItem, BybTextFieldAlt } from '@Components/game/common';
import { IMember } from '@Containers/game/settings/profile';
import Permissions from '@Context/common/permissions/permissions';
import AccountRoles from '@Enums/AccountRoles';
import useI18n from '@i18n';
import { Alert, AlertTitle, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { closeKeyboard } from '@Services/keyboard-service';
import { RoleRecord } from '@Shared/schemas/accounts/accountSchemas';
import { getAllCountryCodes } from '@Shared/utilities/geography';
import { capitalize } from 'lodash-es';
import { ChangeEvent } from 'react';
import { useUnmount } from 'react-use';

interface IProfileDetailsTab {
  memberDetails: IMember | null;
  roles: RoleRecord[];
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const ProfileDetailsTab = ({ memberDetails, roles, onChange }: IProfileDetailsTab) => {
  const { i18n, tCountry } = useI18n();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const avatarSize = isSmDown ? 5 : 10;
  const emailVariant = isSmDown ? 'textLg500' : 'text1xl600';

  const isAccountOwner = memberDetails?.role_id === roles.find(role => role.name === AccountRoles.ACCOUNT_OWNER)?.id;

  const allCountryCodes = getAllCountryCodes();

  useUnmount(() => {
    closeKeyboard();
  });

  return (
    <Stack spacing={4} paddingX={2} boxSizing={'border-box'}>
      <Stack direction="row" spacing={2} alignItems="center">
        <BybAvatar src={memberDetails?.avatar || ''} size={avatarSize} user={memberDetails} />
        <Typography variant={emailVariant} color="dsBase.v200">
          {memberDetails?.email}
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5}>
          <BybTextFieldAlt
            id="firstName"
            name="firstName"
            label={i18n('label.first.name')}
            placeholder={i18n('settings.profile.details.first')}
            value={capitalize(memberDetails?.first_name)}
            onChange={onChange}
            fullWidth
          />
          <BybTextFieldAlt
            id="lastName"
            name="lastName"
            label={i18n('label.last.name')}
            placeholder={i18n('settings.profile.details.last')}
            value={capitalize(memberDetails?.last_name)}
            onChange={onChange}
            fullWidth
          />
        </Stack>

        <BybTextFieldAlt
          name="gender"
          label={i18n('label.gender')}
          value={memberDetails?.gender || 'NA'}
          onChange={onChange}
          fullWidth
          select
        >
          <BybMenuItem id="gender-no-answer" value="NA">
            {i18n('gender.na')}
          </BybMenuItem>
          <BybMenuItem id="gender-male" value="MALE">
            {i18n('gender.male')}
          </BybMenuItem>
          <BybMenuItem id="gender-female" value="FEMALE">
            {i18n('gender.female')}
          </BybMenuItem>
        </BybTextFieldAlt>

        <BybTextFieldAlt
          name="countryCode"
          label={i18n('profile.details.country')}
          value={memberDetails?.country_code || ''}
          onChange={onChange}
          fullWidth
          select
        >
          <BybMenuItem value="">{i18n("profile.settings.country.none")}</BybMenuItem>
          {allCountryCodes.map((code) => (
            <BybMenuItem key={code} value={code}>
              {tCountry(code)}
            </BybMenuItem>
          ))}
        </BybTextFieldAlt>

        <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.GUEST}>
          <BybTextFieldAlt
            name="role"
            label={i18n('label.role')}
            value={memberDetails?.role_id || ''}
            onChange={onChange}
            fullWidth
            disabled={isAccountOwner}
            select
          >
            {roles.map((role) => (
              <BybMenuItem key={role.id} value={role.id} disabled={role.name === AccountRoles.ACCOUNT_OWNER}>
                {i18n(`roles.${role.name}`)}
              </BybMenuItem>
            ))}
          </BybTextFieldAlt>
          {isAccountOwner && 
            <Alert severity='info' sx={{ backgroundColor: 'transparent' }}>
              <AlertTitle>
                {i18n('settings.profile.details.account-owner-warning')}
              </AlertTitle>
            </Alert>
          }
        </Permissions>
      </Stack>
    </Stack>
  );
};

export default ProfileDetailsTab;
